import { useCallback, useRef, useState } from 'react';

import type { SummaryReportNotice, SummaryReportNoticeSeverity } from '../types.ts';

export interface SummaryReportNoticeChannel {
    /** The last thing that happened, or null while nothing has. Outlives its own toast. */
    notice: SummaryReportNotice | null;
    /** Whether the toast for it is still up. */
    toastOpen: boolean;
    notify: (message: string, severity?: SummaryReportNoticeSeverity) => void;
    dismissToast: () => void;
}

export function useSummaryReportNotice(): SummaryReportNoticeChannel {
    const [notice, setNotice] = useState<SummaryReportNotice | null>(null);
    const [toastOpen, setToastOpen] = useState(false);

    // A ref rather than a length or a timestamp: two identical messages a second apart are
    // two notices, and the toast has to be able to tell them apart to reopen.
    const nextId = useRef(0);

    const notify = useCallback(
        (message: string, severity: SummaryReportNoticeSeverity = 'success') => {
            nextId.current += 1;
            setNotice({ id: nextId.current, message, severity });
            setToastOpen(true);
        },
        []
    );

    const dismissToast = useCallback(() => setToastOpen(false), []);

    return { notice, toastOpen, notify, dismissToast };
}
